import {
	MessagingAcceptTransferRequest,
	MessagingRejectTransferRequest,
} from "@/common/models/messagingTransfer";
import { InitiateTransferRequest } from "@/common/types/transfer";
import { Controller, Endpoint } from "..";
import { Client, mapUrl } from "../client";
import {
	AttachmentData,
	ConversationMessages,
	EmailBody,
	EmailOutboundRequest,
	EmailTrustedEndpoint,
	Message,
	ReopenEmailResult,
	SendAttachmentData,
	SendMessageData,
} from "../models/message";
import { TransferStartResponse } from "@/common/types";
import { TransferDestination } from "@/components/organisms/interactionTransferToIvr/types";
import { AxiosResponse } from "axios";
import { AssignInteractionRequest } from "../models/assignInteraction";

export const MessagesKey = "Messages";
export const HistoryMessagesKey = "HistoryMessages";
export const ConversationSummaryKey = "ConversationSummary";
export const CanGenerateConversationSummary = "CanGenerateConversationSummary";
export const AttachmentsKey = "Attachments";
export const InlineAttachmentsKey = "InlineAttachments";
export const EmailEndpointsKey = "EmailEndpointsKey";
export const MessageBodyKey = "MessageBody";

export const fetchConversationMessages = (
	sessionId: string,
	isPreview: boolean,
	signal?: AbortSignal
): Promise<ConversationMessages> => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		isPreview ? Endpoint.Preview : "",
		Endpoint.Messages
	);

	//#warning-ts [bug] in case of timeout, tanstack will do retries forever; the interval between two retries can be of 1 minute. even if the response will return in 30 seconds, it will be considered a timeout, it will be discarded, and another retry will be done.
	return Client.instance.get(url, undefined, signal);
};

export const fetchSessionHistoryMessages = (
	sessionId: string
): Promise<ConversationMessages> => {
	const url = mapUrl(Controller.HistoryEmails, sessionId, Endpoint.Messages);

	return Client.instance.get(url);
};

export const fetchEmailTrustedEndpoints = (): Promise<
	EmailTrustedEndpoint[]
> => {
	return Client.instance.get(
		mapUrl(Controller.Emails, Endpoint.TrustedEndpoints)
	);
};

export const fetchAttachment = (
	sessionId: string,
	isPreview: boolean,
	messageId: string,
	attachmentId: string
): Promise<AttachmentData> => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		isPreview ? Endpoint.Preview : "",
		Endpoint.Messages,
		messageId,
		Endpoint.Attachments,
		attachmentId
	);

	return Client.instance.get(url);
};

export const fetchInlineAttachments = (
	sessionId: string,
	isPreview: boolean,
	messageId: string
): Promise<AttachmentData[]> => {
	const inlineFilter = "inline";
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		isPreview ? Endpoint.Preview : "",
		Endpoint.Messages,
		messageId,
		Endpoint.Attachments,
		inlineFilter
	);

	return Client.instance.get(url);
};

export const fetchHistoryMessageAttachment = (
	sessionId: string,
	messageId: string,
	attachmentId: string
): Promise<AttachmentData> => {
	const url = mapUrl(
		Controller.HistoryEmails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.Attachments,
		attachmentId
	);

	return Client.instance.get(url);
};

export const fetchHistoryMessageInlineAttachments = (
	sessionId: string,
	messageId: string
): Promise<AttachmentData[]> => {
	const inlineFilter = "inline";
	const url = mapUrl(
		Controller.HistoryEmails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.Attachments,
		inlineFilter
	);

	return Client.instance.get(url);
};

export const fetchMessageBody = (
	sessionId: string,
	isPreview: boolean,
	messageId: string
): Promise<EmailBody> => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		isPreview ? Endpoint.Preview : "",
		Endpoint.Messages,
		messageId,
		Endpoint.Body
	);

	return Client.instance.get(url);
};

export const fetchHistoryMessageBody = (
	sessionId: string,
	messageId: string
): Promise<EmailBody> => {
	const url = mapUrl(
		Controller.History,
		"emails",
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.Body
	);

	return Client.instance.get(url);
};

export const initEmailOutbound = (data: EmailOutboundRequest) => {
	return Client.instance.post(mapUrl(Controller.Emails), data);
};

export const send = (
	sessionId: string,
	draftId: string,
	body: SendMessageData
) => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.Messages,
		draftId,
		Endpoint.Send
	);

	return Client.instance.post(url, body);
};

export const singleSend = (sessionId: string, body: SendMessageData) => {
	const url = mapUrl(Controller.Emails, sessionId, Endpoint.SingleSend);

	return Client.instance.post(url, body);
};

export const updateDraft = (
	sessionId: string,
	messageId: string,
	body: SendMessageData,
	signal?: AbortSignal
) => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.ManageDraft
	);

	return Client.instance.post<Message>(url, body, undefined, signal);
};

export const deleteDraft = (sessionId: string, messageId: string) => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.DeleteDraft
	);

	return Client.instance.post(url);
};

export const createReply = (
	sessionId: string,
	messageId: string,
	body: SendMessageData
) => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.CreateReplyDraft
	);

	return Client.instance.post<Message>(url, body);
};

export const createReplyAll = (
	sessionId: string,
	messageId: string,
	body: SendMessageData
) => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.CreateReplyAllDraft
	);

	return Client.instance.post<Message>(url, body);
};

export const createForward = (
	sessionId: string,
	messageId: string,
	body: SendMessageData
) => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.CreateForwardDraft
	);

	return Client.instance.post<Message>(url, body);
};

export const transferEmail = (
	data: InitiateTransferRequest
): Promise<TransferStartResponse> => {
	const url = mapUrl(Controller.Emails, Endpoint.MessagingTransfer);

	return Client.instance.post(url, data);
};

export const acceptEmailConversation = (
	conversationId: string
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.Emails, conversationId, Endpoint.Accept);

	return Client.instance.post(url);
};

export const acceptEmailConversationTransfer = (
	data: MessagingAcceptTransferRequest
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.Emails, Endpoint.MessagingTransferAccept);

	return Client.instance.post(url, data);
};

export const rejectEmailConversation = (
	conversationId: string
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.Emails, conversationId, Endpoint.Reject);

	return Client.instance.post(url);
};

export const rejectEmailConversationTransfer = (
	data: MessagingRejectTransferRequest
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.Emails, Endpoint.MessagingTransferReject);

	return Client.instance.post(url, data);
};

export const markLastEmailAsSeen = (
	conversationId: string
): Promise<AxiosResponse> => {
	const url = mapUrl(
		Controller.Emails,
		conversationId,
		Endpoint.MarkLastMessageAsSeen
	);

	return Client.instance.post(url);
};

export const closeEmailConversation = (
	conversationId: string
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.Emails, conversationId, Endpoint.Close);

	return Client.instance.post(url);
};

export const getEmailTransferDestinations = (): Promise<
	TransferDestination[]
> => {
	const url = mapUrl(Controller.Emails, Endpoint.MessagingTransferDestinations);

	return Client.instance.get(url);
};

export const transferToQueueEmail = (
	data: InitiateTransferRequest
): Promise<TransferStartResponse> => {
	const url = mapUrl(Controller.Emails, Endpoint.MessagingTransferToQueue);

	return Client.instance.post(url, data);
};

export const pickUpEmail = (id: string): Promise<any> => {
	const url = mapUrl(Controller.Emails, Endpoint.PickUp);

	return Client.instance.post(url, id);
};

export const assignEmail = (data: AssignInteractionRequest): Promise<any> => {
	const url = mapUrl(Controller.Emails, Endpoint.Assign);

	return Client.instance.post(url, data);
};

export const startTextMonitoring = (
	sessionId: string,
	supervisorId: string
): Promise<any> => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.EmailMonitoring,
		supervisorId
	);

	return Client.instance.post(url);
};

export const reopenConversation = (
	conversationId: string
): Promise<ReopenEmailResult> => {
	return Client.instance.post<ReopenEmailResult>(
		mapUrl(Controller.Emails, conversationId, Endpoint.ReopenEmailConversation)
	);
};

export const getEmailSummary = (conversationId: string): Promise<string> => {
	return Client.instance.get<string>(
		mapUrl(Controller.Emails, conversationId, Endpoint.Summary)
	);
};

export const pauseInteraction = (conversationId: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.Emails, conversationId, Endpoint.Pause)
	);
};

export const resumeInteraction = (conversationId: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.Emails, conversationId, Endpoint.Resume)
	);
};

export const parkEmail = (conversationId: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.Emails, conversationId, Endpoint.Park)
	);
};

export const unParkEmail = (conversationId: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.Emails, conversationId, Endpoint.UnPark)
	);
};

export const uploadDraftAttachmentFn = (
	sessionId: string,
	messageId: string,
	attachment: SendAttachmentData
) => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.AttachmentUpload
	);

	return Client.instance.post<string>(url, attachment);
};

export const deleteDraftAttachmentFn = (
	sessionId: string,
	messageId: string,
	attachmentId: string,
) => {
	const url = mapUrl(
		Controller.Emails,
		sessionId,
		Endpoint.Messages,
		messageId,
		Endpoint.AttachmentDelete,
		attachmentId
	);

	return Client.instance.post(url);
};

export const previewStarted = (
	conversationId: string
) => {
	return Client.instance.post(
		mapUrl(Controller.Emails, conversationId, Endpoint.PreviewStarted)
	);
};

export const previewStopped = (
	conversationId: string
) => {
	return Client.instance.post(
		mapUrl(Controller.Emails, conversationId, Endpoint.PreviewStopped)
	);
};